'use client';

/**
 * Marketing top navigation — sticky glassmorphism mega-menu, lang switcher, auth CTA.
 *
 * @spec /doc/sprints/future/S79-public-site-ia-scaffolding.md
 * @userStory US-EP033-004
 * @ddl none
 *
 * Renders only on (public)/[locale] route group. Auth state via a lightweight
 * client fetch to /api/auth/me. Desktop: grouped mega-menu (Platform · Solutions ·
 * Method · Resources · Offre). Mobile: accordion drawer.
 */

import { useEffect, useState } from 'react';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Link } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import { Logo } from '@/components/marketing/brand/logo';
import { LangSwitcherV2 } from '@/components/marketing/ui/lang-switcher-v2';
import { Button } from '@/components/marketing/ui/button';
import { cn } from '@/lib/utils';

interface NavLink {
  href: string;
  key: string;
}
interface NavGroup {
  key: string;
  items: NavLink[];
}

/** Grouped mega-menu structure. i18n: titles `groups.<key>`, links `links.<key>`. */
const NAV_GROUPS: NavGroup[] = [
  {
    key: 'platform',
    items: [
      { href: '/platform', key: 'platform' },
      { href: '/agents', key: 'agents' },
      { href: '/agentic-commerce', key: 'agenticCommerce' },
    ],
  },
  {
    key: 'solutions',
    items: [
      { href: '/solutions', key: 'solutions' },
      { href: '/case-studies', key: 'caseStudies' },
    ],
  },
  {
    key: 'method',
    items: [
      { href: '/methodology', key: 'methodology' },
      { href: '/aeo', key: 'aeo' },
      { href: '/geo', key: 'geo' },
      { href: '/share-of-voice', key: 'sov' },
    ],
  },
  {
    key: 'resources',
    items: [
      { href: '/glossary', key: 'glossary' },
      { href: '/compare', key: 'compare' },
      { href: '/answers', key: 'answers' },
      { href: '/resources', key: 'resources' },
    ],
  },
  {
    key: 'offer',
    items: [
      { href: '/offer', key: 'offer' },
      { href: '/pilot', key: 'pilot' },
    ],
  },
];

export function MarketingNav() {
  const t = useTranslations('marketing.nav');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const [authState, setAuthState] = useState<'loading' | 'anon' | 'authed'>('loading');

  useEffect(() => {
    let cancelled = false;
    fetch('/api/auth/me', { credentials: 'include' })
      .then((r) => (cancelled ? null : setAuthState(r.ok ? 'authed' : 'anon')))
      .catch(() => !cancelled && setAuthState('anon'));
    return () => {
      cancelled = true;
    };
  }, []);

  // Close drawer + open menu on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setDrawerOpen(false);
        setOpenGroup(null);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const ctaHref = authState === 'authed' ? '/dashboard' : '/login';
  const ctaKey = authState === 'authed' ? 'dashboard' : 'login';

  return (
    <>
      <header
        className={cn(
          'fixed inset-x-0 top-0 z-50 h-14 lg:h-16',
          'border-b border-white/5 bg-ink-950/60 backdrop-blur-md'
        )}
        onMouseLeave={() => setOpenGroup(null)}
      >
        <nav className="mx-auto flex h-full max-w-[1280px] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <Link href="/" className="flex items-center" aria-label="SkuLift home">
            <Logo variant="wordmark" theme="dark" size={1.6} />
          </Link>

          {/* Desktop mega-menu */}
          <ul className="hidden items-center gap-6 lg:flex">
            {NAV_GROUPS.map((group) => {
              const isOpen = openGroup === group.key;
              return (
                <li
                  key={group.key}
                  className="relative"
                  onMouseEnter={() => setOpenGroup(group.key)}
                >
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    onClick={() => setOpenGroup(isOpen ? null : group.key)}
                    className="inline-flex items-center gap-1 rounded text-sm text-white/80 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
                  >
                    {t(`groups.${group.key}`)}
                    <ChevronDown
                      size={14}
                      aria-hidden="true"
                      className={cn('transition-transform', isOpen && 'rotate-180')}
                    />
                  </button>
                  {isOpen && (
                    <div className="absolute left-0 top-full pt-3">
                      <ul className="min-w-[220px] rounded-xl border border-white/10 bg-ink-950/95 p-2 shadow-glow-cyan-md backdrop-blur-md">
                        {group.items.map((item) => (
                          <li key={item.key}>
                            <Link
                              href={item.href as never}
                              onClick={() => setOpenGroup(null)}
                              className="block rounded-lg px-3 py-2 text-sm text-white/80 transition-colors hover:bg-white/5 hover:text-cyan-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
                            >
                              {t(`links.${item.key}`)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="hidden items-center gap-3 lg:flex">
            <LangSwitcherV2 />
            <Button as="a" href={ctaHref} variant="primary" size="sm">
              {t(ctaKey)}
            </Button>
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            aria-label="Open navigation"
            aria-expanded={drawerOpen}
            aria-controls="mobile-drawer"
            onClick={() => setDrawerOpen(true)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 lg:hidden"
          >
            <Menu size={22} aria-hidden="true" />
          </button>
        </nav>
      </header>

      {/* Mobile drawer — accordion. Rendered as sibling of <header> because the
          header's backdrop-filter creates a containing block that would clip a
          fixed descendant to the header's height. */}
      {drawerOpen && (
        <div
          id="mobile-drawer"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
          className="fixed inset-0 z-[60] flex flex-col bg-ink-950/95 backdrop-blur-sm lg:hidden"
        >
          <div className="flex h-14 items-center justify-between border-b border-white/5 px-4">
            <Link href="/" onClick={() => setDrawerOpen(false)}>
              <Logo variant="wordmark" theme="dark" size={1.6} />
            </Link>
            <button
              type="button"
              aria-label="Close navigation"
              onClick={() => setDrawerOpen(false)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
            >
              <X size={22} aria-hidden="true" />
            </button>
          </div>

          <ul className="flex flex-1 flex-col gap-1 overflow-y-auto px-4 py-6">
            {NAV_GROUPS.map((group) => {
              const isOpen = openAccordion === group.key;
              return (
                <li key={group.key}>
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setOpenAccordion(isOpen ? null : group.key)}
                    className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-base text-white/90 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
                  >
                    {t(`groups.${group.key}`)}
                    <ChevronDown
                      size={18}
                      aria-hidden="true"
                      className={cn('transition-transform', isOpen && 'rotate-180')}
                    />
                  </button>
                  {isOpen && (
                    <ul className="ml-3 border-l border-white/10 pl-3">
                      {group.items.map((item) => (
                        <li key={item.key}>
                          <Link
                            href={item.href as never}
                            onClick={() => setDrawerOpen(false)}
                            className="block rounded-lg px-3 py-2.5 text-sm text-white/80 hover:bg-white/5 hover:text-cyan-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
                          >
                            {t(`links.${item.key}`)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="flex items-center justify-between gap-3 border-t border-white/5 px-4 py-4">
            <LangSwitcherV2 />
            <Button
              as="a"
              href={ctaHref}
              variant="primary"
              size="md"
              onClick={() => setDrawerOpen(false)}
            >
              {t(ctaKey)}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
